const isPositiveNumber = (value) =>
  value !== "" && value != null && Number.isFinite(Number(value)) && Number(value) > 0;

const isPositiveInteger = (value) =>
  isPositiveNumber(value) && Number.isInteger(Number(value));

// A numeric value loaded into the editor is committed; an empty value starts a
// fresh edit session. Once an input event is applied, its provenance becomes
// user, so subsequent keystrokes update that session instead of restarting it.
// A TGW edit also remembers which existing measurement was its source. This is
// important for normal multi-keystroke input: after `4` calculates a package
// weight from count, `40` must continue using count rather than treating the
// calculated package weight as a new source.
const valueProvenance = (value) => (value === "" || value == null ? "empty" : "persisted");

const field = (
  value,
  isValid,
  touched = false,
  provenance = valueProvenance(value),
  calculationSource = null,
) => ({
  value,
  isValid,
  touched,
  provenance,
  calculationSource,
});

const calculatedPackageWeight = (count, tgw) =>
  Number(((Number(count) * Number(tgw)) / 1000).toFixed(6));

const calculatedSeedCount = (packageWeight, tgw) =>
  Math.round((Number(packageWeight) / Number(tgw)) * 1000);

export const createMeasurementState = ({ count = "", tgw = "", packageWeight = "" } = {}) => ({
  count: field(count ?? "", isPositiveInteger(count)),
  tgw: field(tgw ?? "", tgw == null || tgw === "" || isPositiveNumber(tgw)),
  packageWeight: field(
    packageWeight ?? "",
    packageWeight == null || packageWeight === "" || isPositiveNumber(packageWeight),
  ),
});

export const applyMeasurementChange = (state, changedField, rawValue) => {
  const next = {
    count: { ...state.count },
    tgw: { ...state.tgw },
    packageWeight: { ...state.packageWeight },
  };
  const empty = rawValue === "" || rawValue == null;
  const numericValue = empty ? "" : Number(rawValue);

  if (changedField === "count") {
    const valid = isPositiveInteger(numericValue);
    next.count = field(valid ? numericValue : "", valid, true, "user");
    if (valid && isPositiveNumber(next.tgw.value)) {
      next.packageWeight = field(calculatedPackageWeight(numericValue, next.tgw.value), true, false, "calculated");
    }
    return next;
  }

  if (changedField === "tgw") {
    const isCommittedValue = state.tgw.provenance === "persisted";
    if (!isPositiveNumber(numericValue)) {
      // Keep the source while an initially empty TGW is being edited, so
      // clearing an intermediate value and continuing to type does not switch
      // from count to the package weight calculated from that count.
      next.tgw = field("", empty, true, "user", isCommittedValue ? null : state.tgw.calculationSource);
      if (!empty && isCommittedValue) {
        next.count = field("", false, false, "calculated");
        next.packageWeight = field("", true, false, "calculated");
      }
      return next;
    }

    const calculationSource = state.tgw.calculationSource ??
      (isPositiveNumber(next.packageWeight.value) ? "packageWeight" :
        isPositiveInteger(next.count.value) ? "count" : null);

    next.tgw = field(numericValue, true, true, "user", calculationSource);
    // A persisted Count+TGW-only value is the one exception to the usual
    // "changing TGW clears the derived values" rule: Package Weight is
    // missing, so keep the count as the source and fill/recalculate it.
    if (isCommittedValue && calculationSource !== "count") {
      next.count = field("", false, false, "calculated");
      next.packageWeight = field("", true, false, "calculated");
    } else if (calculationSource === "packageWeight" && isPositiveNumber(next.packageWeight.value)) {
      next.count = field(calculatedSeedCount(next.packageWeight.value, numericValue), true, false, "calculated");
    } else if (calculationSource === "count" && isPositiveInteger(next.count.value)) {
      next.packageWeight = field(calculatedPackageWeight(next.count.value, numericValue), true, false, "calculated");
    }
    return next;
  }

  if (changedField === "packageWeight") {
    if (!isPositiveNumber(numericValue)) {
      next.packageWeight = field("", empty, true, "user");
      return next;
    }

    next.packageWeight = field(numericValue, true, true, "user");
    if (isPositiveNumber(next.tgw.value)) {
      next.count = field(calculatedSeedCount(numericValue, next.tgw.value), true, false, "calculated");
    }
    return next;
  }

  throw new Error(`Unknown measurement field: ${changedField}`);
};

// Ends the TGW input session without changing its value or the values that
// were calculated while typing. A later TGW change is therefore recognized as
// a change to a committed value and follows the clearing rules for existing
// TGW values.
export const commitMeasurementState = (state) => ({
  ...state,
  tgw: {
    ...state.tgw,
    provenance: isPositiveNumber(state.tgw.value) ? "persisted" : "empty",
    calculationSource: null,
  },
});

export const areMeasurementsValid = (state) =>
  state.count.isValid && state.tgw.isValid && state.packageWeight.isValid;

export const toMeasurementRequest = (state) => ({
  count: Number(state.count.value),
  tgw: isPositiveNumber(state.tgw.value) ? Number(state.tgw.value) / 1000 : null,
  packageWeight: isPositiveNumber(state.packageWeight.value)
    ? Number(state.packageWeight.value) / 1000
    : null,
});
